import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DataGridPro,
  GridColDef,
  GridToolbar,
  type GridColumnVisibilityModel,
} from "@mui/x-data-grid-pro";

export type PayrollRow = {
  id: string;
  employer_name: string;
  institution_name: string;
  pay_period_start_date: string | null;
  pay_period_end_date: string | null;
  pay_date: string | null;
  paystub_date: string | null;
  sort_date: string | null;
  pay_frequency: string;
  gross_pay_amount: number | null;
  net_pay_amount: number | null;
};

export const parseDateParts = (value?: string | null) => {
  if (!value || typeof value !== "string") return null;
  const datePart = value.split("T")[0];
  const parts = datePart.split("-");
  if (parts.length !== 3) return null;
  const year = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10);
  const day = Number.parseInt(parts[2], 10);
  if (!year || !month || !day) return null;
  return { year, month, day };
};

export const parseDateOnlyToLocalDate = (value?: string | null) => {
  const parts = parseDateParts(value);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
};

export const formatDate = (value?: string | Date | null) => {
  if (!value) return "-";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "-";
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const dd = String(value.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${value.getFullYear()}`;
  }
  const parts = parseDateParts(value);
  if (parts) {
    const mm = String(parts.month).padStart(2, "0");
    const dd = String(parts.day).padStart(2, "0");
    return `${mm}/${dd}/${parts.year}`;
  }
  return value;
};

export const formatCurrency = (value?: number | null) => {
  if (value == null) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const buildPayrollRows = (statements: any[] = []) =>
  statements.map((statement, index) => ({
    id: statement?.id || `payroll-${index}`,
    employer_name: statement?.employer_name || "-",
    institution_name: statement?.institution_name || "-",
    pay_period_start_date: statement?.pay_period?.start_date || null,
    pay_period_end_date: statement?.pay_period?.end_date || null,
    pay_date: statement?.pay_date || null,
    paystub_date: statement?.paystub_date || null,
    sort_date:
      statement?.pay_date ||
      statement?.pay_period?.end_date ||
      statement?.pay_period?.start_date ||
      statement?.paystub_date ||
      null,
    pay_frequency: statement?.pay_frequency || "-",
    gross_pay_amount: statement?.gross_pay?.amount ?? null,
    net_pay_amount: statement?.net_pay?.amount ?? null,
  }));

interface PlaidPayrollIncomeTableProps {
  payrollStatements?: any[];
  isLoading?: boolean;
  isError?: boolean;
}

const GRID_OVERHEAD_PX = 24;
const GRID_ROW_HEIGHT_PX = 52;
const GRID_SCROLL_THRESHOLD_ROWS = 8;
const GRID_FIXED_HEIGHT_PX = 560;

const WRAPPER_STYLE: React.CSSProperties = {
  width: "100%",
  minWidth: 0,
};

const GRID_STYLE: React.CSSProperties = {
  width: "100%",
};

type ColumnSpec = {
  field: keyof PayrollRow;
  width: number;
  hideRank?: number;
  colDef: Omit<GridColDef<PayrollRow>, "field" | "width">;
};

const COLUMN_SPECS: ColumnSpec[] = [
  {
    field: "employer_name",
    width: 220,
    hideRank: 3,
    colDef: { headerName: "Employer" },
  },
  {
    field: "institution_name",
    width: 160,
    hideRank: 1,
    colDef: { headerName: "Payroll Processor" },
  },
  {
    field: "pay_period_start_date",
    width: 120,
    hideRank: 4,
    colDef: {
      headerName: "Period Start Date",
      type: "date",
      valueGetter: (value) => parseDateOnlyToLocalDate(value as string | null),
      valueFormatter: (value) => formatDate(value as Date | null),
    },
  },
  {
    field: "pay_period_end_date",
    width: 104,
    hideRank: 5,
    colDef: {
      headerName: "Pay End Date",
      type: "date",
      valueGetter: (value) => parseDateOnlyToLocalDate(value as string | null),
      valueFormatter: (value) => formatDate(value as Date | null),
    },
  },
  {
    field: "pay_date",
    width: 110,
    colDef: {
      headerName: "Pay Date",
      type: "date",
      valueGetter: (value) => parseDateOnlyToLocalDate(value as string | null),
      valueFormatter: (value) => formatDate(value as Date | null),
    },
  },
  {
    field: "paystub_date",
    width: 124,
    hideRank: 2,
    colDef: {
      headerName: "Paystub Date",
      type: "date",
      valueGetter: (value) => parseDateOnlyToLocalDate(value as string | null),
      valueFormatter: (value) => formatDate(value as Date | null),
    },
  },
  {
    field: "sort_date",
    width: 1,
    colDef: {
      headerName: "Sort Date",
      type: "date",
      valueGetter: (value) => parseDateOnlyToLocalDate(value as string | null),
    },
  },
  {
    field: "gross_pay_amount",
    width: 114,
    colDef: {
      headerName: "Gross Pay",
      type: "number",
      valueFormatter: (value) => formatCurrency(value as number | null),
    },
  },
  {
    field: "net_pay_amount",
    width: 114,
    colDef: {
      headerName: "Net Pay",
      type: "number",
      valueFormatter: (value) => formatCurrency(value as number | null),
    },
  },
];

const COLUMNS: GridColDef<PayrollRow>[] = COLUMN_SPECS.map(
  ({ field, width, colDef }) => ({
    field: field as string,
    width,
    ...colDef,
  })
);

const AUTO_HIDE_SPECS: ColumnSpec[] = COLUMN_SPECS.filter(
  (column) => typeof column.hideRank === "number"
).sort((a, b) => (a.hideRank as number) - (b.hideRank as number));

function computeAutoVisibilityModel(
  containerWidth: number,
  baseModel: GridColumnVisibilityModel
): GridColumnVisibilityModel {
  if (!containerWidth) return baseModel;

  const available = Math.max(0, containerWidth - GRID_OVERHEAD_PX);
  const visible = new Set(
    COLUMN_SPECS
      .filter((column) => baseModel[column.field as string] !== false)
      .map((column) => column.field as string)
  );
  let total = COLUMN_SPECS.reduce((sum, column) => {
    const field = column.field as string;
    return visible.has(field) ? sum + column.width : sum;
  }, 0);

  for (const spec of AUTO_HIDE_SPECS) {
    if (total <= available) break;
    const field = spec.field as string;
    if (visible.delete(field)) {
      total -= spec.width;
    }
  }

  const model: GridColumnVisibilityModel = {};
  for (const spec of COLUMN_SPECS) {
    const field = spec.field as string;
    model[field] = visible.has(field);
  }

  return model;
}

const PlaidPayrollIncomeTable = ({
  payrollStatements,
  isLoading = false,
  isError = false,
}: PlaidPayrollIncomeTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const update = () => setContainerWidth(element.getBoundingClientRect().width);
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const rows = useMemo(
    () => buildPayrollRows(Array.isArray(payrollStatements) ? payrollStatements : []),
    [payrollStatements]
  );

  const usePaystubDateOnlyMode = useMemo(() => {
    const hasPaystubDate = rows.some((row) => Boolean(row.paystub_date));
    const hasPayDate = rows.some((row) => Boolean(row.pay_date));
    const hasPayPeriod = rows.some(
      (row) => Boolean(row.pay_period_start_date) || Boolean(row.pay_period_end_date)
    );
    return hasPaystubDate && !hasPayDate && !hasPayPeriod;
  }, [rows]);
  const usePaystubDateWhenPayDateMissing = useMemo(() => {
    const hasPaystubDate = rows.some((row) => Boolean(row.paystub_date));
    const hasPayDate = rows.some((row) => Boolean(row.pay_date));
    return hasPaystubDate && !hasPayDate;
  }, [rows]);
  const hasPayrollProcessor = useMemo(
    () =>
      rows.some((row) => {
        const value = String(row.institution_name || "").trim();
        return Boolean(value && value !== "-");
      }),
    [rows]
  );

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          acc.grossTotal += row.gross_pay_amount || 0;
          acc.netTotal += row.net_pay_amount || 0;
          return acc;
        },
        { grossTotal: 0, netTotal: 0 }
      ),
    [rows]
  );
  const summaryRow = useMemo(
    () => ({
      id: "summary",
      employer_name: "Totals",
      institution_name: "",
      pay_period_start_date: null,
      pay_period_end_date: null,
      pay_date: null,
      paystub_date: null,
      sort_date: null,
      pay_frequency: "",
      gross_pay_amount: totals.grossTotal,
      net_pay_amount: totals.netTotal,
    }),
    [totals]
  );

  const [userVisibilityModel, setUserVisibilityModel] =
    useState<GridColumnVisibilityModel | null>(null);
  const baseVisibilityModel = useMemo(() => {
    const model: GridColumnVisibilityModel = {};
    COLUMN_SPECS.forEach((column) => {
      model[column.field as string] = true;
    });
    // Hidden by default unless paystub_date is the only pay/check date Argyle provided.
    model.paystub_date = false;
    model.sort_date = false;
    if (usePaystubDateWhenPayDateMissing) {
      model.paystub_date = true;
      model.pay_date = false;
    }
    if (usePaystubDateOnlyMode) {
      model.paystub_date = true;
      model.pay_period_start_date = false;
      model.pay_period_end_date = false;
      model.pay_date = false;
    }
    if (!hasPayrollProcessor) {
      model.institution_name = false;
    }
    return model;
  }, [hasPayrollProcessor, usePaystubDateOnlyMode, usePaystubDateWhenPayDateMissing]);
  const autoVisibilityModel = useMemo(
    () => computeAutoVisibilityModel(containerWidth, baseVisibilityModel),
    [containerWidth, baseVisibilityModel]
  );
  const effectiveVisibilityModel = userVisibilityModel ?? autoVisibilityModel;
  const useFixedScrollableGrid = rows.length > GRID_SCROLL_THRESHOLD_ROWS;

  const handleQuickFilterKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      // Keep Enter in grid filter from bubbling to the surrounding form submit handler.
      event.preventDefault();
      event.stopPropagation();
    }
  };

  if (isError) return <p>Unable to load payroll records.</p>;
  if (isLoading) return <p>Loading payroll records...</p>;
  if (!rows.length) return <p>No payroll records linked yet.</p>;

  return (
    <div style={WRAPPER_STYLE} ref={containerRef}>
      <DataGridPro
        rows={rows}
        columns={COLUMNS}
        autoHeight={!useFixedScrollableGrid}
        hideFooter
        rowHeight={GRID_ROW_HEIGHT_PX}
        pinnedRows={{ bottom: [summaryRow] }}
        columnVisibilityModel={effectiveVisibilityModel}
        onColumnVisibilityModelChange={(model) => {
          setUserVisibilityModel(model);
        }}
        style={
          useFixedScrollableGrid
            ? { ...GRID_STYLE, height: GRID_FIXED_HEIGHT_PX, maxHeight: GRID_FIXED_HEIGHT_PX }
            : GRID_STYLE
        }
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: {
              placeholder: "Filter",
              onKeyDown: handleQuickFilterKeyDown,
            },
            csvOptions: { allColumns: true },
            printOptions: { disableToolbarButton: true },
          },
        }}
        initialState={{
          sorting: { sortModel: [{ field: "sort_date", sort: "asc" }] },
        }}
      />
    </div>
  );
};

export default PlaidPayrollIncomeTable;
